/**
 * Shared File Preview Renderer
 * Pure DOM rendering utilities used by both FileViewerManager and CompareFileViewer.
 * Does not handle modal sizing, navigation, or column-specific state.
 */
var FilePreviewRenderer = (function() {
  'use strict';

  function clearNode(node) {
    if (!node) return;
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function renderImage(shell, name, url) {
    clearNode(shell);
    var img = new Image();
    img.alt = name || 'File preview';
    img.src = url;
    img.className = 'max-h-[75vh] w-auto max-w-full object-contain';
    shell.appendChild(img);
    return img;
  }

  function renderDocument(shell, name, url) {
    clearNode(shell);
    var iframe = document.createElement('iframe');
    iframe.title = name || 'File preview';
    iframe.src = url;
    iframe.loading = 'lazy';
    iframe.className = 'h-[75vh] w-full rounded-md border border-gray-200 bg-white';
    shell.appendChild(iframe);
    return iframe;
  }

  function renderDocx(shell, name, url, callbacks) {
    clearNode(shell);
    var loading = document.createElement('div');
    loading.className = 'flex min-h-[24rem] items-center justify-center';
    loading.innerHTML = '<p class="text-sm text-gray-500">Loading document preview...</p>';
    shell.appendChild(loading);

    var token = callbacks && callbacks.renderToken ? callbacks.renderToken : null;

    function isStale() {
      return token && shell.getAttribute('data-file-viewer-render-token') !== token;
    }

    var abortController = new AbortController();

    function abortIfStale() {
      if (isStale()) {
        abortController.abort();
      }
    }

    fetch(url, { credentials: 'same-origin', signal: abortController.signal })
      .then(function(response) {
        abortIfStale();
        if (isStale()) throw new Error('stale render');
        if (!response.ok) throw new Error('Failed to load');
        return response.arrayBuffer();
      })
      .then(function(buf) {
        abortIfStale();
        if (isStale()) throw new Error('stale render');
        // NOTE: These dynamic imports are code-split by Vite. If the
        // production build does not emit separate chunks, these will 404.
        // See vite.config.ts manualChunks configuration.
        return import('mammoth').then(function(mod) {
          return (mod.default || mod).convertToHtml({ arrayBuffer: buf });
        });
      })
      .then(function(result) {
        abortIfStale();
        if (isStale()) throw new Error('stale render');
        return import('dompurify').then(function(DOMPurifyModule) {
          abortIfStale();
          if (isStale()) throw new Error('stale render');
          var DOMPurify = DOMPurifyModule.default || DOMPurifyModule;
          var rawHtml = result && result.value ? result.value : '';
          var sanitizedHtml = DOMPurify.sanitize(rawHtml, { WHOLE_DOCUMENT: false, RETURN_DOM_FRAGMENT: false, FORBID_TAGS: ['form'] });

          var html = '<!doctype html><html><head><meta charset="utf-8"><style>body{margin:0;padding:1rem;font-family:system-ui,sans-serif;font-size:14px;line-height:1.5;color:#111827;} img{max-width:100%;height:auto;}</style></head><body>' + sanitizedHtml + '</body></html>';

          clearNode(shell);
          var iframe = document.createElement('iframe');
          iframe.title = name || 'File preview';
          // SECURITY: allow-same-origin lets this iframe share the parent origin.
          // It is required so the parent can read contentDocument.scrollWidth
          // for responsive sizing. Because we also set srcdoc (not src) and
          // sanitize through DOMPurify before insertion, XSS risk is mitigated.
          // INVARIANT: never add allow-scripts alongside allow-same-origin.
          // Doing so would let sanitized content reach parent cookies and storage.
          // TODO: Rewrite sizing to use postMessage so allow-same-origin can
          // be removed entirely.
          iframe.sandbox = 'allow-same-origin';
          iframe.srcdoc = html;
          iframe.className = 'h-[75vh] w-full rounded-md border border-gray-200 bg-white';
          shell.appendChild(iframe);

          if (callbacks && callbacks.onLoad) {
            var callOnLoad = function() {
              callbacks.onLoad(iframe);
            };
            if (iframe.contentDocument && iframe.contentDocument.readyState === 'complete') {
              callOnLoad();
            } else {
              iframe.addEventListener('load', callOnLoad);
            }
          }
        });
      })
      .catch(function() {
        if (isStale()) return;
        clearNode(shell);
        if (callbacks && callbacks.onError) {
          callbacks.onError();
        } else {
          renderFallback(shell, name, 'Preview for this document could not be loaded.');
        }
      });
  }

  function renderFallback(shell, name, message) {
    clearNode(shell);
    var wrapper = document.createElement('div');
    wrapper.className = 'flex min-h-[24rem] w-full items-center justify-center rounded-md border border-dashed border-gray-300 bg-white p-6 text-center';
    var content = document.createElement('div');
    var title = document.createElement('p');
    var body = document.createElement('p');
    title.className = 'text-sm font-medium text-gray-900';
    title.textContent = name || 'File preview';
    body.className = 'mt-1 text-sm text-gray-500';
    body.textContent = message || 'Preview for this file type is not supported.';
    content.appendChild(title);
    content.appendChild(body);
    wrapper.appendChild(content);
    shell.appendChild(wrapper);
  }

  function renderFile(shell, name, url, mode, callbacks) {
    if (mode === 'image') { renderImage(shell, name, url); return; }
    if (mode === 'docx') { renderDocx(shell, name, url, callbacks); return; }
    if (mode === 'document') { renderDocument(shell, name, url); return; }
    renderFallback(shell, name);
  }

  return {
    clearNode: clearNode,
    escapeHtml: escapeHtml,
    renderImage: renderImage,
    renderDocument: renderDocument,
    renderDocx: renderDocx,
    renderFallback: renderFallback,
    renderFile: renderFile
  };
})();

window.FilePreviewRenderer = FilePreviewRenderer;
